import { useEffect, useRef } from 'react';
import './NoiseShader.scss';

const NoiseShaderTVGrainAnimation = ({ visible }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const mousePosRef = useRef({ x: 0.5, y: 0.5 });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) {
            console.error('WebGL not supported');
            return;
        }

        // Vertex shader
        const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

        // Fragment shader with TV Grain effect
        const fragmentShaderSource = `
      precision mediump float;
      uniform float time;
      uniform vec2 resolution;
      uniform float grainScale;
      uniform float grainDensity;

      // Random function
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      void main() {
        // Mouse X → grain scale (coarse left, fine right)
        // Mouse Y → grain density (dense top, sparse bottom)
        float grain = random(gl_FragCoord.xy * grainScale + mod(time * 10.0, 100.0));
        
        // Pink color: #ff87c3
        vec3 pink = vec3(1.0, 0.529, 0.765);
        
        float alpha = grain * grain * grainDensity;
        
        gl_FragColor = vec4(pink, alpha);
      }
    `;

        // Compile shader helper
        const compileShader = (source, type) => {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, source);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error('Shader compile failed:', gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        };

        const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
        const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

        if (!vertexShader || !fragmentShader) return;

        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error('Program link failed:', gl.getProgramInfoLog(program));
            return;
        }

        gl.useProgram(program);

        // Set up rectangle (2 triangles) covering the canvas
        const vertices = new Float32Array([
            -1.0, -1.0,
            1.0, -1.0,
            -1.0, 1.0,
            -1.0, 1.0,
            1.0, -1.0,
            1.0, 1.0,
        ]);

        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        const positionLocation = gl.getAttribLocation(program, 'position');
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

        const timeLocation = gl.getUniformLocation(program, 'time');
        const resolutionLocation = gl.getUniformLocation(program, 'resolution');
        const grainScaleLocation = gl.getUniformLocation(program, 'grainScale');
        const grainDensityLocation = gl.getUniformLocation(program, 'grainDensity');

        const startTime = Date.now();
        let currentScale = 1.5;
        let currentDensity = 0.85;

        const handleMouseMove = (e) => {
            mousePosRef.current = {
                x: e.clientX / window.innerWidth,
                y: e.clientY / window.innerHeight,
            };
        };
        window.addEventListener('mousemove', handleMouseMove);

        const render = () => {
            // Resize canvas if display size changes using devicePixelRatio for sharper/finer grain
            const dpr = window.devicePixelRatio || 1;
            const displayWidth = Math.floor(canvas.clientWidth * dpr);
            const displayHeight = Math.floor(canvas.clientHeight * dpr);

            if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
                canvas.width = displayWidth;
                canvas.height = displayHeight;
                gl.viewport(0, 0, canvas.width, canvas.height);
            }

            // Smooth lerp toward mouse-driven targets
            const { x, y } = mousePosRef.current;
            const targetScale = 0.6 + x * 2.8;          // 0.6 (coarse) → 3.4 (fine)
            const targetDensity = 0.2 + (1 - y) * 1.3;  // 0.2 (sparse bottom) → 1.5 (dense top)
            currentScale += (targetScale - currentScale) * 0.06;
            currentDensity += (targetDensity - currentDensity) * 0.06;

            const currentTime = (Date.now() - startTime) / 1000;

            gl.uniform1f(timeLocation, currentTime);
            gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
            gl.uniform1f(grainScaleLocation, currentScale);
            gl.uniform1f(grainDensityLocation, currentDensity);

            gl.drawArrays(gl.TRIANGLES, 0, 6);

            animationRef.current = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            gl.deleteProgram(program);
            gl.deleteShader(vertexShader);
            gl.deleteShader(fragmentShader);
            gl.deleteBuffer(positionBuffer);
        };
    }, []);

    const canvasStyle = {
        opacity: visible ? 1 : 0,
        transition: visible ? 'opacity 3s ease' : 'opacity 2s ease',
    };

    return <canvas ref={canvasRef} className="noise-shader" style={canvasStyle} />;
};

export default NoiseShaderTVGrainAnimation;
